import usePopup from '../../../hooks/usePopup';
import React, { useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import Form from '../../../kingpin/forms/Form';
import FormHeader from '../../../kingpin/forms/FormHeader';
import FormContent from '../../../kingpin/forms/FormContent';
import TextInput from '../../../kingpin/atoms/TextInput';
import { ListItemWrapper } from '../../../components/IndexListItem';
import UserIcon from '../../../components/Common/UserIcon';
import Typography from '../../../kingpin/atoms/Typography';
import { GroupsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';

const MembershipPopup = ({
  group,
  close,
}: {
  group: UserManagement.Group;
  close: () => void;
}) => {
  const { getUsersInGroup } = useContext(GroupsContext);

  const [searchText, setSearchText] = useState<string>('');

  const baseMembers = useMemo((): (
    | UserManagement.PendingUser
    | UserManagement.SignedUpUser
  )[] => {
    return getUsersInGroup(group.id);
  }, [getUsersInGroup, group.id]);

  const members = useMemo((): (
    | UserManagement.PendingUser
    | UserManagement.SignedUpUser
  )[] => {
    if (searchText === '') {
      return baseMembers;
    }

    return baseMembers.filter((user) =>
      user.displayName.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [baseMembers, searchText]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader title={`"${group.name}" members`} onClose={close} />
        <FormContent>
          <div style={{ marginBottom: 16 }}>
            <TextInput
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              icon={'search'}
              placeholder="Search..."
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 400,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                overflowY: 'scroll',
              }}
              data-testid={'group-members'}
            >
              {members.length === 0 && (
                <Typography.Header type={'H3'}>
                  No users found
                </Typography.Header>
              )}
              {members.map((user) => (
                <ListItemWrapper
                  key={user.id}
                  style={{ gap: 8, justifyContent: 'flex-start' }}
                >
                  <UserIcon {...user} />
                  <Typography.Body type={'Button Text'}>
                    {user.displayName}
                  </Typography.Body>
                </ListItemWrapper>
              ))}
            </div>
          </div>
        </FormContent>
      </Form>
    </Modal>
  );
};

const V3GroupNameWithMembershipPopup = ({
  group,
}: {
  group: UserManagement.Group;
}) => {
  const { isOpen, open, close } = usePopup();

  return (
    <>
      <Typography.Body
        type={'Link'}
        onClick={open}
        data-testid={`group-info-${group.id}`}
      >
        {group.name}
      </Typography.Body>
      <ModalTransition>
        {isOpen && <MembershipPopup close={close} group={group} />}
      </ModalTransition>
    </>
  );
};

export default V3GroupNameWithMembershipPopup;
